import * as Sentry from "@sentry/react";

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
}

// Thresholds based on Google's Core Web Vitals
const thresholds = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

function getRating(name: string, value: number): "good" | "needs-improvement" | "poor" {
  const threshold = thresholds[name as keyof typeof thresholds];
  if (!threshold) return "good";
  
  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
}

function reportToSentry(metric: WebVitalsMetric) {
  // Only report if Sentry is initialized
  if (typeof Sentry.addBreadcrumb === "function") {
    Sentry.addBreadcrumb({
      category: "web-vitals",
      message: `${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
      level: metric.rating === "poor" ? "warning" : "info",
      data: {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      },
    });

    // Report poor metrics as custom measurements
    if (metric.rating === "poor") {
      Sentry.setMeasurement(metric.name, metric.value, "millisecond");
    }
  }
}

function createMetricHandler(name: string) {
  return (entry: PerformanceEntry) => {
    const value = name === "CLS" 
      ? (entry as PerformanceEntry & { value?: number }).value ?? 0
      : entry.startTime;
    
    const metric: WebVitalsMetric = {
      name,
      value,
      rating: getRating(name, value),
      delta: value,
      id: `${name}-${Date.now()}`,
    };

    reportToSentry(metric);

    // Log in development
    if (import.meta.env.DEV) {
      console.log(`[Web Vitals] ${name}:`, {
        value: value.toFixed(2),
        rating: metric.rating,
      });
    }
  };
}

export function initPerformanceMonitoring() {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
    return;
  }

  // Largest Contentful Paint
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        createMetricHandler("LCP")(lastEntry);
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (e) {
    // LCP not supported
  }

  // First Input Delay / Interaction to Next Paint
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEventTiming;
        const value = fidEntry.processingStart - fidEntry.startTime;
        createMetricHandler("FID")({ ...entry, startTime: value } as PerformanceEntry);
      });
    });
    fidObserver.observe({ type: "first-input", buffered: true });
  } catch (e) {
    // FID not supported
  }

  // Cumulative Layout Shift
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries() as (PerformanceEntry & { value?: number; hadRecentInput?: boolean })[];
      entries.forEach((entry) => {
        if (!entry.hadRecentInput && entry.value) {
          clsValue += entry.value;
        }
      });
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });

    // Report CLS on page hide
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        const metric: WebVitalsMetric = {
          name: "CLS",
          value: clsValue,
          rating: getRating("CLS", clsValue),
          delta: clsValue,
          id: `CLS-${Date.now()}`,
        };
        reportToSentry(metric);
      }
    });
  } catch (e) {
    // CLS not supported
  }

  // First Contentful Paint
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntriesByName("first-contentful-paint");
      if (entries.length > 0) {
        createMetricHandler("FCP")(entries[0]);
      }
    });
    fcpObserver.observe({ type: "paint", buffered: true });
  } catch (e) {
    // FCP not supported
  }

  // Time to First Byte
  try {
    const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const ttfb = navEntries[0].responseStart;
      createMetricHandler("TTFB")({ startTime: ttfb } as PerformanceEntry);
    }
  } catch (e) {
    // TTFB not supported
  }
}
